import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { AccountBookmarkModel, BOOKMARK_TYPE, DocumentModel } from '@/lib/models';
import { constructUrl, formOptionToMultiSelectOption, getParsedQuery, getValidPaginatorSize } from '@/lib/utils';
import { documentService } from '@/lib/services';
import { useHandleError, useQuery } from '@/hooks';
import {
	flattenParentAndChildIdsForSelection,
	getTopLevelParentAndChildrenIds,
	LinkBox,
	Loader,
	LoadingButton,
	MultiSelect,
	MultiSelectOption,
	RecentlyViewed,
	ResearchListItem,
	Select,
} from '@/components';

export const Research = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const handleError = useHandleError();

	const query = useQuery();
	const hasQuery = useMemo(() => {
		const paramsToIgnore = ['size'];

		return (
			Object.entries(getParsedQuery(query)).filter(
				([key, value]) => !paramsToIgnore.includes(key) && value !== ''
			).length > 0
		);
	}, [query]);
	const solutionIntelligenceQuery = useMemo(
		() =>
			query
				.get('solutionIntelligence')
				?.split('|')
				.filter((s) => s),
		[query]
	);
	const solutionVendorsQuery = useMemo(
		() =>
			query
				.get('solutionVendors')
				?.split('|')
				.filter((s) => s),
		[query]
	);
	const topicsQuery = useMemo(
		() =>
			query
				.get('topics')
				?.split('|')
				.filter((s) => s),
		[query]
	);
	const parentBusinessProcessesQuery = useMemo(
		() =>
			query
				.get('parentBusinessProcesses')
				?.split('|')
				.filter((s) => s),
		[query]
	);
	const businessProcessesQuery = useMemo(
		() =>
			query
				.get('businessProcesses')
				?.split('|')
				.filter((s) => s),
		[query]
	);
	const contentTypesQuery = useMemo(
		() =>
			query
				.get('contentTypes')
				?.split('|')
				.filter((s) => s),
		[query]
	);
	const size = useMemo(() => getValidPaginatorSize(query), [query]);
	const pageRef = useRef(0);

	const [isLoadingFilters, setIsLoadingFilters] = useState(false);

	const [solutionIntelligenceFilters, setSolutionIntelligenceFilters] = useState<MultiSelectOption[]>([]);
	const [solutionVendorFilters, setSolutionVendorFilters] = useState<MultiSelectOption[]>([]);
	const [topicFilters, setTopicFilters] = useState<MultiSelectOption[]>([]);
	const [businessProcessFilters, setBusinessProcessFilters] = useState<MultiSelectOption[]>([]);
	const [contentTypeFilters, setContentTypeFilters] = useState<MultiSelectOption[]>([]);

	const [research, setResearch] = useState<DocumentModel[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [loadingResearch, setLoadingResearch] = useState(false);

	const fetchFilters = useCallback(async () => {
		try {
			setIsLoadingFilters(true);

			const response = await documentService
				.getDocumentFilters({
					...(topicsQuery && { topicIds: topicsQuery }),
					...(parentBusinessProcessesQuery && { parentBusinessProcessIds: parentBusinessProcessesQuery }),
					...(businessProcessesQuery && { businessProcessIds: businessProcessesQuery }),
				})
				.fetch();

			setSolutionIntelligenceFilters(formOptionToMultiSelectOption(response.solutionIntelligence ?? []));
			setSolutionVendorFilters(formOptionToMultiSelectOption(response.vendors ?? []));
			setTopicFilters(formOptionToMultiSelectOption(response.topics ?? []));
			setBusinessProcessFilters(formOptionToMultiSelectOption(response.businessProcesses ?? []));
			setContentTypeFilters(formOptionToMultiSelectOption(response.contentTypes ?? []));
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoadingFilters(false);
		}
	}, [businessProcessesQuery, handleError, parentBusinessProcessesQuery, topicsQuery]);

	const allBpSelections = useMemo(() => {
		return flattenParentAndChildIdsForSelection(
			businessProcessFilters,
			parentBusinessProcessesQuery,
			businessProcessesQuery
		);
	}, [businessProcessFilters, businessProcessesQuery, parentBusinessProcessesQuery]);

	useEffect(() => {
		fetchFilters();
	}, [fetchFilters]);

	const fetchResearch = useCallback(async () => {
		try {
			setLoadingResearch(true);

			const response = await documentService
				.getDocuments({
					...(topicsQuery && { topicIds: topicsQuery }),
					...(parentBusinessProcessesQuery && { parentBusinessProcessIds: parentBusinessProcessesQuery }),
					...(businessProcessesQuery && { businessProcessIds: businessProcessesQuery }),
					...(contentTypesQuery && { documentTypes: contentTypesQuery }),
					...(solutionIntelligenceQuery && { solutionIntelligenceIds: solutionIntelligenceQuery }),
					...(solutionVendorsQuery && { vendorIds: solutionVendorsQuery }),
					size: size,
					page: `${pageRef.current}`,
				})
				.fetch();

			setTotalCount(response.totalCount);
			setResearch((previousValue) => {
				if (pageRef.current === 0) {
					return response.documents;
				}

				return previousValue.concat(response.documents);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setLoadingResearch(false);
		}
	}, [
		businessProcessesQuery,
		contentTypesQuery,
		handleError,
		parentBusinessProcessesQuery,
		size,
		solutionIntelligenceQuery,
		solutionVendorsQuery,
		topicsQuery,
	]);

	useEffect(() => {
		pageRef.current = 0;
	}, [
		businessProcessesQuery,
		contentTypesQuery,
		parentBusinessProcessesQuery,
		solutionIntelligenceQuery,
		solutionVendorsQuery,
		topicsQuery,
	]);

	useEffect(() => {
		fetchResearch();
	}, [fetchResearch]);

	const handleClearButtonClick = () => {
		pageRef.current = 0;
		navigate(pathname, { replace: true });
	};

	const handleSolutionIntelligenceChange = (selectedSolutionIntelligenceIds: string[]) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { solutionIntelligence: selectedSolutionIntelligenceIds }, search), {
			replace: true,
		});
	};

	const handleSolutionVendorsChange = (selectedSolutionVendorIds: string[]) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { solutionVendors: selectedSolutionVendorIds }, search), {
			replace: true,
		});
	};

	const handleTopicsChange = (selectedTopicIds: string[]) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { topics: selectedTopicIds }, search), { replace: true });
	};

	const handleBusinessProcessesChange = useCallback(
		(selectedBpIds: string[]) => {
			const { parentIds, remainingChildrenIds } = getTopLevelParentAndChildrenIds(
				businessProcessFilters,
				selectedBpIds
			);

			pageRef.current = 0;
			navigate(
				constructUrl(
					pathname,
					{
						parentBusinessProcesses: parentIds,
						businessProcesses: remainingChildrenIds,
					},
					search
				),
				{ replace: true }
			);
		},
		[businessProcessFilters, navigate, pathname, search]
	);

	const handleContentTypesChange = (selectedContentTypeIds: string[]) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { contentTypes: selectedContentTypeIds }, search), { replace: true });
	};

	const handleShowMoreButtonClick = () => {
		pageRef.current += 1;
		fetchResearch();
	};

	const handlePageSizeChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		pageRef.current = 0;
		navigate(constructUrl(pathname, { size: currentTarget.value }, search), { replace: true });
	};

	const handleResearchListItemChange = (bookmark: AccountBookmarkModel) => {
		setResearch((previousValue) =>
			previousValue.map((d) => {
				return d.documentId === bookmark.bookmarkContent.documentId ? bookmark.bookmarkContent : d;
			})
		);
	};

	return (
		<>
			<Helmet>
				<title>Hackett Connect - Research</title>
			</Helmet>

			<Container className="pt-7">
				<Row>
					<Col>
						<div className="d-flex align-items-center justify-content-between">
							<h1 className="mb-0 text-primary">
								{loadingResearch ? (
									<>Loading...</>
								) : (
									<>{hasQuery ? `Filtered Research` : 'All Research'}</>
								)}
							</h1>
						</div>
					</Col>
				</Row>
			</Container>

			<Container className="pt-7 pb-16">
				<Row>
					<Col xs={12} xl={8} className="mb-6">
						<div className="mb-6 d-flex align-items-center justify-content-end">
							<div className="d-flex align-items-center">
								<Form.Label className="mb-0">Page Size</Form.Label>
								<Select variant="secondary" value={size} onChange={handlePageSizeChange}>
									<option value="10">10</option>
									<option value="20">20</option>
									<option value="50">50</option>
								</Select>
							</div>
						</div>
						{!loadingResearch && research.length === 0 && (
							<p className="mb-2 text-muted text-center">
								<strong>No Results</strong>
							</p>
						)}
						{research.map((document) => (
							<ResearchListItem
								key={document.documentId}
								documentId={document.documentId}
								category={document.documentTypeName}
								fileType={document.fileType}
								viewUrl={document.viewUrl ?? ''}
								shareUrl={document.relativeUrl}
								downloadUrl={document.downloadUrl ?? ''}
								canDownload={document.canDownload}
								bookmarkType={BOOKMARK_TYPE.DOCUMENT}
								isBookmarked={document.bookmarkedFlag}
								to={`/research/${document.documentId}`}
								title={document.name}
								description={document.listDescription ?? ''}
								imageSrc={document.imageUrl ?? ''}
								date={document.publicationDateDescription}
								tags={document.topicTags.map((tag) => tag.name)}
								onChange={handleResearchListItemChange}
								isPodcast={!!document.podcastUrl}
							/>
						))}
						{loadingResearch && research.length <= 0 && (
							<div className="mb-8 d-flex justify-content-center">
								<Loader />
							</div>
						)}
						<hr className="mb-8" />
						<div className="d-flex align-items-center justify-content-end">
							<span className="text-muted mr-4">
								Showing 1-{research.length}
								{hasQuery && <>&nbsp;of {totalCount}</>}
							</span>
							{research.length < totalCount && (
								<LoadingButton
									isLoading={loadingResearch}
									variant="outline-info"
									onClick={handleShowMoreButtonClick}
								>
									Show {size} More
								</LoadingButton>
							)}
						</div>
					</Col>
					<Col xs={12} xl={4} className="mb-6">
						<Card className="mb-2">
							<Card.Body>
								<div className="mb-4 d-flex align-items-end justify-content-between">
									<h3 className="mb-0">Filters</h3>
									<Button variant="link" onClick={handleClearButtonClick}>
										Clear
									</Button>
								</div>

								<Form.Group className="mb-6">
									<Form.Label>Topics</Form.Label>
									<MultiSelect
										searchEnabled
										options={topicFilters}
										selected={topicsQuery ?? []}
										onChange={handleTopicsChange}
										disabled={isLoadingFilters}
									>
										Select
									</MultiSelect>
								</Form.Group>
								<Form.Group className="mb-6">
									<Form.Label>Business Processes</Form.Label>
									<MultiSelect
										searchEnabled
										options={businessProcessFilters}
										selected={allBpSelections}
										onChange={handleBusinessProcessesChange}
										disabled={isLoadingFilters}
									>
										Select
									</MultiSelect>
								</Form.Group>
								{solutionIntelligenceFilters.length > 0 && (
									<Form.Group className="mb-6">
										<Form.Label>Solution Intelligence</Form.Label>
										<MultiSelect
											searchEnabled
											options={solutionIntelligenceFilters}
											selected={solutionIntelligenceQuery ?? []}
											onChange={handleSolutionIntelligenceChange}
											disabled={isLoadingFilters}
										>
											Select
										</MultiSelect>
									</Form.Group>
								)}
								{solutionVendorFilters.length > 0 && (
									<Form.Group className="mb-6">
										<Form.Label>Solution Vendors</Form.Label>
										<MultiSelect
											searchEnabled
											options={solutionVendorFilters}
											selected={solutionVendorsQuery ?? []}
											onChange={handleSolutionVendorsChange}
											disabled={isLoadingFilters}
										>
											Select
										</MultiSelect>
									</Form.Group>
								)}
								<Form.Group>
									<Form.Label>Content Type</Form.Label>
									<MultiSelect
										searchEnabled
										options={contentTypeFilters}
										selected={contentTypesQuery ?? []}
										onChange={handleContentTypesChange}
										disabled={isLoadingFilters}
									>
										Select
									</MultiSelect>
								</Form.Group>
							</Card.Body>
						</Card>
						<RecentlyViewed className="mb-2" />
						<LinkBox
							sections={[
								{
									title: "Can't find what you're looking for?",
									links: [{ title: 'Contact Hackett', to: '/contact', showIcon: true }],
								},
							]}
						/>
					</Col>
				</Row>
			</Container>
		</>
	);
};
